import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Users, Send, Loader2, CheckCircle2, Clock } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { CoverImage } from '../components/CoverImage';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { displayClanName } from '../lib/displayName';
import type { Me } from '../lib/api';

export type ClanApplyState = 'applied' | 'closed' | 'apply' | 'login';

// ─────────────────────────────────────────────────────────────────────────────
// Apply-affordance gate for the browse-clans card grid (JJN test, 2026-07-26:
// the QATest dev account, role:'public' with zero CLAN# rows, hit "Login to
// apply" despite holding a fully valid, re-derived-live authenticated
// session — role:'public' just means "not yet in a clan", not "logged out").
// The backend's POST /clans/:name/applications (portalApi/index.ts ~14453,
// "Any authenticated user can apply.") checks only clan-exists +
// publicApplicationsOpen + not-already-member + not-banned — it has NO role
// check. This must never be stricter than that: gate on authentication
// (`!!me`) plus the same real business conditions the backend enforces,
// never on `role`.
// ─────────────────────────────────────────────────────────────────────────────
export function clanApplyState(args: {
  me: Pick<Me, 'role'> | null | undefined;
  applicationsOpen: boolean;
  alreadyApplied: boolean;
}): ClanApplyState {
  if (args.alreadyApplied) return 'applied';
  if (!args.applicationsOpen) return 'closed';
  if (!args.me) return 'login';
  return 'apply';
}

export function BrowseClans() {
  const [query, setQuery] = useState('');
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: browseData, isLoading } = useQuery({
    queryKey: ['browseClans'],
    queryFn: api.browseClans,
  });
  const { data: myApps } = useQuery({
    queryKey: ['myApplications'],
    queryFn: api.myApplications,
    // JJN test, 2026-07-26: was gated on role !== 'public' too — a
    // role:'public' caller (not yet in any clan) can still have applications
    // on file (the QATest account had a live pending CLAN application), so
    // gating this fetch on role hid their own application from them.
    enabled: !!me,
  });

  const applications = myApps?.applications ?? [];
  const pendingClans = new Set(applications.filter(a => a.status === 'PENDING').map(a => a.clanName));

  const clans = (browseData?.clans ?? []).filter(c => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return c.name.toLowerCase().includes(q)
      || displayClanName(c.name).toLowerCase().includes(q)
      || (c.description ?? '').toLowerCase().includes(q);
  });

  if (isLoading) return <PageSpinner />;

  return (
    <div className="max-w-5xl">
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Search className="h-7 w-7 text-ink-secondary" />
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink-primary">Browse Clans</h1>
          <p className="text-xs text-ink-meta">Every clan on the platform — recruiting clans take applications</p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          id="browse-clans-intro"
          title="How applications work"
          intro="Every clan is listed; ones marked recruiting take applications. Apply with your gamertag — if the leader accepts, you're in. Your pending applications show at the top."
          points={[
            { text: 'Applications stay open for 30 days, then auto-close.' },
            { text: 'You can have multiple pending applications. First leader to accept wins.' },
          ]}
          className="mb-6"
        />
      </div>

      {/* My pending applications */}
      {applications.length > 0 && (
        <div className={cn('mb-6', SETTLE)} style={settleDelay(2)}>
          <Card>
            <h2 className="mb-3 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-ink-secondary">
              <Clock className="h-3.5 w-3.5" strokeWidth={2.5} />
              Your Applications
            </h2>
            <div className="space-y-2">
              {applications.map(app => (
                <div
                  key={app.clanId}
                  className="flex items-center justify-between rounded-lg bg-white/[0.04] px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-display text-sm font-black tracking-wide text-ink-primary">
                      {displayClanName(app.clanName)}
                    </p>
                    <p className="font-mono text-[10px] text-ink-meta">
                      Submitted {new Date(app.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={
                      app.status === 'PENDING'
                        ? 'rounded-full bg-amber-500/10 px-2.5 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-amber-300'
                        : app.status === 'ACCEPTED'
                        ? 'rounded-full bg-emerald-500/10 px-2.5 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-300'
                        : 'rounded-full bg-red-500/10 px-2.5 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-red-300'
                    }
                  >
                    {app.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className={cn('mb-6', SETTLE)} style={settleDelay(3)}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by clan name or description"
          className="input w-full max-w-md"
        />
      </div>

      {clans.length === 0 ? (
        <div className={SETTLE} style={settleDelay(4)}>
          <Card className="text-center">
            <p className="text-sm text-ink-secondary">
              {query.trim() ? 'No clans match your search.' : 'No clans exist yet — be the first to forge one.'}
            </p>
          </Card>
        </div>
      ) : (
        <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', SETTLE)} style={settleDelay(4)}>
          {clans.map(clan => (
            <BrowseClanCard
              key={clan.name}
              clan={clan}
              applyState={clanApplyState({
                me,
                applicationsOpen: clan.applicationsOpen,
                alreadyApplied: pendingClans.has(clan.name),
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BrowseClanCard({
  clan,
  applyState,
}: {
  clan: { name: string; description?: string; leaderId: string; memberCount: number; applicationsOpen: boolean };
  applyState: ClanApplyState;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [gamertag, setGamertag] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { data: assets } = useQuery({
    queryKey: ['clanAssets', clan.name],
    queryFn: () => api.listAssets('clan', clan.name),
  });

  const sorted = [...(assets?.assets ?? [])].sort((a, b) => b.createdAt - a.createdAt);
  const banner = sorted.find(a => a.assetType === 'banner');
  const logo = sorted.find(a => a.assetType === 'logo');

  const applyMut = useMutation({
    mutationFn: () => api.applyToClan(clan.name, { gamertag: gamertag.trim() || undefined, message: message.trim() || undefined }),
    onSuccess: () => {
      setOpen(false);
      setError('');
      qc.invalidateQueries({ queryKey: ['myApplications'] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-lg bg-paper-elevated shadow-[0_1px_2px_0_rgba(0,0,0,0.4)] transition-colors', MORPH)}>
      {/* Banner */}
      {/* 32/7 = half the old 16/7 height — Lisa: the banner was very big.
          Default banner = the clan's own logo, centre-cropped edge-to-edge
          under a heavy blur (scale hides the blur's edge fringe). A real
          uploaded banner always wins; owners customise via clan assets. */}
      <div className="relative aspect-[32/7] w-full overflow-hidden bg-paper-inset">
        {banner ? (
          <CoverImage src={banner.url} alt={`${clan.name} banner`} objectPosition={banner.objectPosition} className="h-full w-full object-cover" />
        ) : logo ? (
          <CoverImage
            src={logo.url}
            alt=""
            aria-hidden
            objectPosition={logo.objectPosition}
            className="h-full w-full scale-110 object-cover opacity-90 blur-lg"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center opacity-30">
            <Users className="h-8 w-8 text-ink-secondary" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          {/* relative z-10: the banner above is position:relative and would
              otherwise paint OVER the logo tile that pulls up into it. */}
          <div className="relative z-10 -mt-8 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-paper-panel shadow-[0_1px_2px_0_rgba(0,0,0,0.4)]">
            {logo ? (
              <CoverImage src={logo.url} alt={`${clan.name} logo`} objectPosition={logo.objectPosition} className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-base font-black uppercase text-ink-secondary">
                {displayClanName(clan.name).slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            {/* no CSS `uppercase`: brand casings like M.o.M must render as-is
                (stored legacy names are already caps, so they look the same) */}
            <h3 className="truncate font-display text-lg font-black tracking-tight text-ink-primary">
              {displayClanName(clan.name)}
            </h3>
            <p className={cn('font-mono text-[9px] uppercase tracking-wider', clan.applicationsOpen ? 'text-emerald-300/80' : 'text-ink-meta')}>
              {clan.applicationsOpen ? 'Recruiting — open to applications' : 'Applications closed'}
            </p>
          </div>
        </div>

        {clan.description && (
          <p className="line-clamp-3 text-sm text-ink-body">{clan.description}</p>
        )}

        <div className="mt-auto flex items-center gap-2">
          <Link
            to={`/clan/${encodeURIComponent(clan.name)}`}
            className="flex-1 rounded-md bg-white/[0.05] px-3 py-2 text-center font-display text-[10px] font-black uppercase tracking-wider text-ink-body transition-colors hover:bg-white/[0.08] hover:text-ink-primary"
          >
            View
          </Link>
          {applyState === 'applied' ? (
            <span className="flex-1 rounded-md bg-amber-500/10 px-3 py-2 text-center font-display text-[10px] font-black uppercase tracking-wider text-amber-300">
              Applied
            </span>
          ) : applyState === 'closed' ? (
            <span className="flex-1 rounded-md bg-white/[0.04] px-3 py-2 text-center font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">
              Closed
            </span>
          ) : applyState === 'apply' ? (
            <button
              onClick={() => setOpen(true)}
              className={cn('flex-1 rounded-md bg-accent-500 px-3 py-2 font-display text-[10px] font-black uppercase tracking-wider text-[#0a0a0c] transition-colors hover:bg-accent-400', MORPH)}
            >
              Apply
            </button>
          ) : (
            <span className="flex-1 rounded-md bg-white/[0.03] px-3 py-2 text-center font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">
              Login to apply
            </span>
          )}
        </div>
      </div>

      {/* Apply modal */}
      {open && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/70" onClick={() => !applyMut.isPending && setOpen(false)} />
          <div className="fixed left-1/2 top-1/2 z-[80] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-paper-panel p-5 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
            <p className="font-display text-[10px] font-black uppercase tracking-[0.22em] text-ink-meta">
              Apply to
            </p>
            <p className="mt-1 font-display text-xl font-black uppercase tracking-tight text-ink-primary">
              {clan.name}
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="label">
                  Your Gamertag
                </label>
                <input
                  value={gamertag}
                  onChange={e => setGamertag(e.target.value)}
                  placeholder="Gamertag#tag"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="label">
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Tell the leader why you want in"
                  className="input w-full resize-none"
                />
              </div>

              {error && (
                <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {error}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setOpen(false)}
                  disabled={applyMut.isPending}
                  className="btn-ghost flex-1 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={() => applyMut.mutate()}
                  disabled={applyMut.isPending}
                  className={cn('btn-primary flex flex-1 items-center justify-center gap-1.5 disabled:opacity-50', MORPH)}
                >
                  {applyMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                  ) : (
                    <Send className="h-3 w-3" strokeWidth={2.5} />
                  )}
                  Submit
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function BrowseClansSuccess() {
  return (
    <Card>
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
      <p className="mt-3 text-center text-sm text-white">Application sent.</p>
    </Card>
  );
}
